import React, { useEffect, useState, useRef, forwardRef } from "react";
import { getBillDetails } from "../../Api/Api";
import { useNotification } from "../Notification/NotificationContext";
import { useReactToPrint } from "react-to-print";
import html2pdf from "html2pdf.js";
import { FaUser, FaTools, FaCogs, FaFileInvoiceDollar } from "react-icons/fa";

// ---------- Invoice Content ----------
const InvoiceContent = forwardRef(({ bill, billData }, ref) => (
    <div
        ref={ref}
        className="invoice bg-white p-10 text-gray-800 font-sans border rounded-lg shadow-md"
    >
        {/* Header */}
        <div className="flex justify-between items-center border-b pb-4 mb-6">
            <div>
                <h1 className="text-3xl font-bold text-blue-800 flex items-center gap-2">
                    <FaFileInvoiceDollar /> INVOICE
                </h1>
                <p className="text-sm text-gray-600">Tamaria Motors</p>
                <p className="text-sm text-gray-600">Sawai Madhopur, Rajasthan</p>
                <p className="text-sm text-gray-600">Phone: +91-9999431998</p>
            </div>
            <div className="text-right">
                <h2 className="text-xl font-semibold text-blue-700">
                    Invoice #{bill.id}
                </h2>
                <p className="text-sm">Date: {bill.date}</p>
            </div>
        </div>

        {/* Customer Details */}
        <div className="mb-6">
            <h3 className="text-lg font-semibold text-blue-600 mb-2 flex items-center gap-2">
                <FaUser /> Customer
            </h3>
            <div className="text-sm bg-gray-50 p-3 rounded-md">
                <p>
                    <span className="font-medium">Name:</span>{" "}
                    {billData.customer?.name}
                </p>
                <p>
                    <span className="font-medium">Phone:</span>{" "}
                    {billData.customer?.phone}
                </p>
                <p>
                    <span className="font-medium">Address:</span>{" "}
                    {billData.customer?.address}
                </p>
            </div>
        </div>

        {/* Job Card */}
        <div className="mb-6">
            <h3 className="text-lg font-semibold text-blue-600 mb-2 flex items-center gap-2">
                <FaTools /> Job Card
            </h3>
            <div className="text-sm bg-gray-50 p-3 rounded-md">
                <p>
                    <span className="font-medium">Job Card ID:</span>{" "}
                    {billData.jobCard.id}
                </p>
                <p>
                    <span className="font-medium">Date:</span>{" "}
                    {billData.jobCard.date}
                </p>
            </div>
        </div>

        {/* Work Types */}
        <div className="mb-6">
            <h3 className="text-lg font-semibold text-green-600 mb-2 flex items-center gap-2">
                <FaCogs /> Work Done
            </h3>
            <table className="w-full border border-gray-300 text-sm rounded-md overflow-hidden">
                <thead className="bg-gray-100 text-gray-700">
                <tr>
                    <th className="p-2 text-left">Work Type</th>
                    <th className="p-2 text-right">Price</th>
                </tr>
                </thead>
                <tbody>
                {billData.jobCard.workTypes.map((workType, index) => (
                    <tr
                        key={workType.id}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                        <td className="p-2">{workType.name}</td>
                        <td className="p-2 text-right">
                            ₹ {workType.price.toFixed(2)}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>

        {/* Inventory Items */}
        <div className="mb-6">
            <h3 className="text-lg font-semibold text-yellow-600 mb-2 flex items-center gap-2">
                <FaCogs /> Parts & Inventory
            </h3>
            <table className="w-full border border-gray-300 text-sm rounded-md overflow-hidden">
                <thead className="bg-gray-100 text-gray-700">
                <tr>
                    <th className="p-2 text-left">Item</th>
                    <th className="p-2 text-center">Qty</th>
                    <th className="p-2 text-right">Unit Price</th>
                    <th className="p-2 text-right">Total</th>
                </tr>
                </thead>
                <tbody>
                {billData.jobCard.inventoryItems.map((item, index) => (
                    <tr
                        key={index}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                        <td className="p-2">{item.name}</td>
                        <td className="p-2 text-center">{item.quantity}</td>
                        <td className="p-2 text-right">₹ {item.price.toFixed(2)}</td>
                        <td className="p-2 text-right">
                            ₹ {(item.price * item.quantity).toFixed(2)}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
        {bill.extraCharges  > 0 && (
            <div className="mt-2">
                <p className="font-medium text-blue-600">Extra Charges</p>
                <ul className="text-sm space-y-1">
                    <li  className="flex justify-between">
                        <span>{bill.comment}</span>
                        <span>₹ {bill.extraCharges.toFixed(2)}</span>
                    </li>
                </ul>
            </div>
        )}
        {/* Totals */}
        <div className="text-right space-y-1 text-sm mt-6">
            <p>
                <span className="font-medium">Subtotal:</span> ₹{" "}
                {bill.totalAmount.toFixed(2)}
            </p>
            <p>
                <span className="font-medium">Discount:</span> ₹ {bill.discount}
            </p>
            <p className="text-lg font-bold text-green-700">
                Final Amount: ₹ {bill.finalAmount.toFixed(2)}
            </p>
        </div>

        {/* Footer */}
        <div className="border-t mt-8 pt-4 text-sm text-gray-600 text-center">
            <p>Thank you for choosing Tamaria Motors!</p>
            <p>Please contact us for your next service.</p>
        </div>
    </div>
));

// ---------- Main Modal ----------
const BillDetailsModal = ({ show, bill, onClose }) => {
    const [billData, setBillData] = useState(null);
    const { showNotification } = useNotification();
    const invoiceRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => invoiceRef.current,
        documentTitle: `Invoice-${bill.id}`,
    });

    const handleDownloadPDF = () => {
        if (!invoiceRef.current) return;
        const element = invoiceRef.current;
        const opt = {
            margin: 10,
            filename: `Invoice-${bill.id}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        };
        html2pdf().set(opt).from(element).save();
    };

    useEffect(() => {
        if (show && bill.id) {
            getBillDetails(bill.id)
                .then((response) => setBillData(response.data))
                .catch((error) => showNotification(error.message, "error"));
        }
    }, [show, bill]);

    if (!show || !billData) return null;

    return (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white shadow-2xl rounded-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
                {/* Printable Invoice */}
                <InvoiceContent ref={invoiceRef} bill={bill} billData={billData} />

                {/* Action Buttons */}
                <div className="flex justify-end gap-2 mt-4 no-print">
                    <button
                        onClick={handleDownloadPDF}
                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                        Download PDF
                    </button>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BillDetailsModal;
